import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Button } from 'js/design_system/Button';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import { Work, WorkClaim } from './types';
import WorkMetadata from './WorkMetadata';
import PaperThumbnail from './PaperThumbnail';
import trackClick from 'js/utils/trackClick';

type SourcePaperCardProps = {
  sourceClaim: WorkClaim;
  paper: Work;
  showHeader?: boolean;
  thumbnailWidth: number;
  thumbnailHeight: number;
  openCitationsModal: () => void;
};

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs-8, 8px);
  padding-top: var(--spacing-xs-8, 8px);
`;

const CiteClaimButton = styled(Button)`
  border: 1px solid var(--brand-colors-blue-600, #0645b1);
  color: var(--primary-brand, #0645b1);
  font-size: var(--type-sizes-16, 16px);
  line-height: var(--type-line-heights-20, 20px);
  font-weight: 600;
  padding: 0px 20px;
`;

const RelatedPaperRow = styled.div`
  display: flex;
  gap: var(--spacing-sm-24, 24px);
  align-items: flex-start;
  width: 100%;
`;

const SeePaperButton = styled(ButtonLink)`
  font-size: var(--type-sizes-16, 16px);
  line-height: var(--type-line-heights-20, 20px);
  padding: 0px 20px;
`;

const SourceCopy = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
`;

const SourcePaperCard: React.FunctionComponent<SourcePaperCardProps> = ({
  sourceClaim,
  paper,
  showHeader = false,
  thumbnailWidth,
  thumbnailHeight,
  openCitationsModal,
}) => {
  return (
    <>
      <RelatedPaperRow>
        <SourceCopy>
          {showHeader && (
            <Body bold size="md">
              Source paper
            </Body>
          )}
          <BodyLink
            size="md"
            href={paper.url}
            onClick={() => {
              trackClick({
                source: 'single_claim_page.source_paper_card.title.click',
                extra: {
                  source_claim_id: sourceClaim.id,
                },
              });
            }}
          >
            {paper.title}
          </BodyLink>
          <WorkMetadata
            sourceClaim={sourceClaim}
            work={paper}
            metadataLocation="source_paper"
            showTitle={false}
          />
        </SourceCopy>
        <PaperThumbnail
          work={paper}
          paperType="source_paper"
          width={thumbnailWidth}
          height={thumbnailHeight}
        />
      </RelatedPaperRow>
      <ActionRow>
        <CiteClaimButton
          size="sm"
          secondary
          icon="format_quote"
          onClick={openCitationsModal}
        >
          Cite this claim
        </CiteClaimButton>
        <SeePaperButton
          href={paper.url}
          size="sm"
          icon="description"
          onClick={() => {
            trackClick({
              source: 'single_claim_page.source_paper_card.see_full_paper',
              extra: {
                source_claim_id: sourceClaim.id,
              },
            });
          }}
        >
          Read full paper
        </SeePaperButton>
      </ActionRow>
    </>
  );
};

export default SourcePaperCard;
